import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bookmark, Copy, Minus, Package, Plus, Search, ShoppingCart, X } from 'lucide-react';
import { listSites } from '../../api/sites';
import { listSiteCatalog } from '../../api/catalog';
import { createOrder } from '../../api/orders';
import { CatalogItemThumbnail } from '../../components/CatalogItemThumbnail';
import { useAuth } from '../../auth/useAuth';
import { primaryOrgId } from '../../auth/permissions';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import type { CustomerCatalogItem } from '../../types/api';

const ALL_CATEGORIES = 'All';

function money(value: string | number) {
  return `£${Number(value).toFixed(2)}`;
}

function newLineId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `line-${Date.now()}-${Math.random()}`;
}

// One line in the basket - deliberately NOT keyed by itemId alone, so the
// same catalog item can appear as several separate lines (e.g. three of the
// same laptop model, each going to a different named person with its own
// `reference`). `id` is a client-only key for React/state purposes, thrown
// away on submit - the server only ever sees itemId/quantity/reference.
interface CartLine {
  id: string;
  itemId: string;
  quantity: number;
  reference: string;
}

function QuantityStepper({ quantity, onChange }: { quantity: number; onChange: (next: number) => void }) {
  if (quantity <= 0) {
    return (
      <Button size="sm" onClick={() => onChange(1)}>
        <Plus className="h-3.5 w-3.5" /> Add
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white p-1">
      <button
        type="button"
        onClick={() => onChange(quantity - 1)}
        className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="w-6 text-center text-sm font-semibold text-slate-900">{quantity}</span>
      <button
        type="button"
        onClick={() => onChange(quantity + 1)}
        className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// Small inline +/- used inside a single basket line (as opposed to the
// bigger QuantityStepper on the catalog grid) - no "0 removes it" affordance
// here, since removal is its own explicit X button per line.
function MiniStepper({ quantity, onChange }: { quantity: number; onChange: (next: number) => void }) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, quantity - 1))}
        className="flex h-6 w-6 items-center justify-center text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
      >
        <Minus className="h-3 w-3" />
      </button>
      <span className="w-5 text-center text-xs font-semibold text-slate-900">{quantity}</span>
      <button
        type="button"
        onClick={() => onChange(quantity + 1)}
        className="flex h-6 w-6 items-center justify-center text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
      >
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );
}

// Browse + cart + submit, scoped to the user's own site(s) - the "shop
// window" per the design doc (§4.1), backed by GET /catalog/sites/:id/items
// which already respects org-wide vs site-specific visibility server-side.
// Category filter + search are client-side over that one response - the
// catalog per site is small enough that a round-trip per filter isn't worth it.
interface ReorderState {
  siteId: string;
  lines: { customerCatalogItemId: string; quantity: number; reference?: string }[];
}

export function ShopPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const orgId = primaryOrgId(user);
  const savedForLaterKey = user ? `atlas-saved-for-later-${user.id}` : null;

  // "Reorder" from a past order (see OrderDetailPage) arrives as router state
  // rather than a query param, since it carries a full line list. Consumed
  // once, then cleared from history so a refresh/back-nav doesn't reapply it.
  const reorderState = (location.state as { reorder?: ReorderState } | null)?.reorder ?? null;
  const [reorderApplied, setReorderApplied] = useState(!reorderState);

  const [siteId, setSiteId] = useState('');
  const [reference, setReference] = useState('');
  const [cartLines, setCartLines] = useState<CartLine[]>([]);
  // Set aside without being part of the order being built right now - e.g.
  // "we'll need these next month, not today". Persisted per-user in
  // localStorage (same pattern as the auth token) so it survives closing
  // the tab, not just this one visit.
  const [savedForLater, setSavedForLater] = useState<CartLine[]>(() => {
    if (!savedForLaterKey) return [];
    try {
      const raw = localStorage.getItem(savedForLaterKey);
      return raw ? (JSON.parse(raw) as CartLine[]) : [];
    } catch {
      return [];
    }
  });
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!savedForLaterKey) return;
    try {
      localStorage.setItem(savedForLaterKey, JSON.stringify(savedForLater));
    } catch {
      // Storage full/unavailable (e.g. private browsing) - saved-for-later
      // just won't persist across a reload; not worth surfacing an error for.
    }
  }, [savedForLater, savedForLaterKey]);

  const sitesQuery = useQuery({
    queryKey: ['sites', orgId],
    queryFn: () => listSites(orgId!),
    enabled: !!orgId,
  });

  const sites = sitesQuery.data ?? [];
  const effectiveSiteId = siteId || sites[0]?.id || '';

  const catalogQuery = useQuery({
    queryKey: ['site-catalog', effectiveSiteId],
    queryFn: () => listSiteCatalog(effectiveSiteId),
    enabled: !!effectiveSiteId,
  });

  const allItems = catalogQuery.data ?? [];
  const itemById = useMemo(() => new Map(allItems.map((i) => [i.id, i])), [allItems]);

  // Once the catalog for the current site has loaded, silently drop any
  // saved-for-later entry whose item no longer exists or isn't visible any
  // more - same "skip and don't complain loudly" approach as the reorder
  // flow below, since this can only be discovered lazily as catalogs load.
  useEffect(() => {
    if (catalogQuery.isLoading || !catalogQuery.data) return;
    setSavedForLater((prev) => prev.filter((l) => itemById.get(l.itemId)?.visible));
  }, [catalogQuery.isLoading, catalogQuery.data, itemById]);

  const categories = useMemo(() => {
    const names = new Set<string>();
    for (const item of allItems) {
      if (item.category) names.add(item.category);
    }
    return [ALL_CATEGORIES, ...Array.from(names).sort()];
  }, [allItems]);

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allItems.filter((item) => {
      if (category !== ALL_CATEGORIES && item.category !== category) return false;
      if (term && !item.name.toLowerCase().includes(term) && !item.description?.toLowerCase().includes(term)) {
        return false;
      }
      return true;
    });
  }, [allItems, category, search]);

  // Reset back to "All" if the selected category no longer has any items
  // (e.g. switching site) - never leave the shop looking silently empty.
  useEffect(() => {
    if (category !== ALL_CATEGORIES && !categories.includes(category)) {
      setCategory(ALL_CATEGORIES);
    }
  }, [category, categories]);

  // Step 1 of applying a reorder: switch to the original order's site (if
  // still accessible) so its catalog loads - the actual cart is filled in
  // by the effect below, once that catalog has arrived.
  useEffect(() => {
    if (!reorderState || reorderApplied || !sitesQuery.data) return;
    if (!sites.some((s) => s.id === reorderState.siteId)) {
      showToast("Can't reorder - you no longer have access to that order's site.");
      setReorderApplied(true);
      return;
    }
    setSiteId(reorderState.siteId);
  }, [reorderState, reorderApplied, sitesQuery.data]);

  // Step 2: once the catalog for that site has loaded, fill the basket from
  // the past order's lines (one basket line per original order line, each
  // keeping its own reference) - skipping anything that's been deleted or
  // made unorderable since, and telling the user how many were skipped.
  useEffect(() => {
    if (!reorderState || reorderApplied) return;
    if (effectiveSiteId !== reorderState.siteId) return;
    if (catalogQuery.isLoading || !catalogQuery.data) return;

    const newLines: CartLine[] = [];
    let skipped = 0;
    for (const line of reorderState.lines) {
      const item = allItems.find((i) => i.id === line.customerCatalogItemId);
      if (item && item.visible) {
        newLines.push({ id: newLineId(), itemId: item.id, quantity: line.quantity, reference: line.reference ?? '' });
      } else {
        skipped += 1;
      }
    }
    setCartLines(newLines);
    setReorderApplied(true);
    showToast(
      skipped > 0
        ? `Reordered ${newLines.length} line(s) - ${skipped} from the original order ${skipped === 1 ? 'is' : 'are'} no longer available and were skipped.`
        : `Reordered ${newLines.length} line(s) from your past order.`,
    );
    // Clear the navigation state so a refresh or back-navigation doesn't
    // silently reapply the same reorder over whatever's in the basket by then.
    navigate(location.pathname, { replace: true, state: null });
  }, [reorderState, reorderApplied, effectiveSiteId, catalogQuery.isLoading, catalogQuery.data, allItems]);

  const resolvedCartLines = useMemo(
    () =>
      cartLines
        .map((l) => ({ line: l, item: itemById.get(l.itemId) }))
        .filter((l): l is { line: CartLine; item: CustomerCatalogItem } => !!l.item),
    [cartLines, itemById],
  );

  const cartTotal = resolvedCartLines.reduce((sum, l) => sum + Number(l.item.price) * l.line.quantity, 0);
  const cartItemCount = resolvedCartLines.reduce((sum, l) => sum + l.line.quantity, 0);

  // Total quantity across every basket line for one item - what the
  // catalog grid's stepper displays, since several lines can now share the
  // same item.
  function totalQuantityForItem(itemId: string) {
    return cartLines.filter((l) => l.itemId === itemId).reduce((sum, l) => sum + l.quantity, 0);
  }

  const mutation = useMutation({
    mutationFn: () =>
      createOrder({
        siteId: effectiveSiteId,
        reference,
        lines: resolvedCartLines.map((l) => ({
          customerCatalogItemId: l.item.id,
          quantity: l.line.quantity,
          reference: l.line.reference.trim() || undefined,
        })),
      }),
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      showToast(
        order.status === 'AUTO_APPROVED'
          ? `Order "${order.reference}" submitted and auto-approved.`
          : `Order "${order.reference}" submitted for approval.`,
      );
      setCartLines([]);
      setReference('');
      navigate(`/orders/${order.id}`);
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not submit the order.')),
  });

  // Grid stepper's "+": bump the first existing basket line for this item,
  // or start a new one at quantity 1 if there isn't one yet. Splitting off
  // a genuinely separate line (e.g. for a different recipient) is done from
  // the basket panel via "Add another", not from here - keeps the common
  // "just add more of this" case a single click.
  function incrementItem(itemId: string) {
    setCartLines((prev) => {
      const existing = prev.find((l) => l.itemId === itemId);
      if (existing) {
        return prev.map((l) => (l.id === existing.id ? { ...l, quantity: l.quantity + 1 } : l));
      }
      return [...prev, { id: newLineId(), itemId, quantity: 1, reference: '' }];
    });
  }

  // Grid stepper's "-": trim from the LAST line for this item first (most
  // recently added / least likely to already carry a carefully-typed
  // reference), removing it entirely once it hits zero.
  function decrementItem(itemId: string) {
    setCartLines((prev) => {
      const matches = prev.filter((l) => l.itemId === itemId);
      const target = matches[matches.length - 1];
      if (!target) return prev;
      if (target.quantity <= 1) {
        return prev.filter((l) => l.id !== target.id);
      }
      return prev.map((l) => (l.id === target.id ? { ...l, quantity: l.quantity - 1 } : l));
    });
  }

  function updateLineQuantity(lineId: string, quantity: number) {
    setCartLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, quantity: Math.max(1, Math.floor(quantity) || 1) } : l)));
  }

  function updateLineReference(lineId: string, ref: string) {
    setCartLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, reference: ref } : l)));
  }

  function removeLine(lineId: string) {
    setCartLines((prev) => prev.filter((l) => l.id !== lineId));
  }

  // "Add another" - splits off a fresh, separate line for the same item
  // (quantity 1, blank reference) so it can go to a different named person
  // without disturbing the line it was copied from.
  function duplicateLine(lineId: string) {
    setCartLines((prev) => {
      const source = prev.find((l) => l.id === lineId);
      if (!source) return prev;
      const idx = prev.findIndex((l) => l.id === lineId);
      const copy: CartLine = { id: newLineId(), itemId: source.itemId, quantity: 1, reference: '' };
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });
  }

  function saveLineForLater(lineId: string) {
    setCartLines((prev) => {
      const line = prev.find((l) => l.id === lineId);
      if (!line) return prev;
      setSavedForLater((saved) => [...saved, line]);
      return prev.filter((l) => l.id !== lineId);
    });
  }

  function moveSavedToCart(lineId: string) {
    setSavedForLater((prev) => {
      const line = prev.find((l) => l.id === lineId);
      if (!line) return prev;
      setCartLines((cart) => [...cart, { ...line, id: newLineId() }]);
      return prev.filter((l) => l.id !== lineId);
    });
  }

  function removeSaved(lineId: string) {
    setSavedForLater((prev) => prev.filter((l) => l.id !== lineId));
  }

  function handleSubmit() {
    setError(null);
    if (!reference.trim()) {
      setError('Please say who this order is for.');
      return;
    }
    if (resolvedCartLines.length === 0) {
      setError('Add at least one item to your order.');
      return;
    }
    mutation.mutate();
  }

  if (!orgId) return null;

  return (
    <div>
      <PageHeader title="Shop" description="Browse what's available at your site and submit a new order." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          {sites.length > 1 ? (
            <Select label="Site" value={effectiveSiteId} onChange={(e) => setSiteId(e.target.value)}>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          ) : (
            sites.length === 1 && (
              // Shown even with just one site - otherwise it's not obvious
              // which site's catalog you're looking at, and an item scoped
              // to a *different* site (via its Availability setting) would
              // just silently not appear with no clue why.
              <p className="text-sm text-slate-500">
                Shopping for <span className="font-medium text-slate-700">{sites[0].name}</span>
              </p>
            )
          )}

          {allItems.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                />
              </div>

              {categories.length > 2 && (
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCategory(c)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                        category === c
                          ? 'bg-brand-600 text-white'
                          : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {catalogQuery.isLoading ? (
            <Card>
              <div className="py-14 text-center">
                <Spinner />
              </div>
            </Card>
          ) : allItems.length === 0 ? (
            <Card>
              <EmptyState
                icon={Package}
                title="Nothing available to order yet"
                description="Ask your MSP to add catalog items for your site."
              />
            </Card>
          ) : visibleItems.length === 0 ? (
            <Card>
              <EmptyState icon={Search} title="No items match" description="Try a different search term or category." />
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {visibleItems.map((item) => (
                <Card key={item.id} className="flex flex-col overflow-hidden">
                  <div className="flex items-start gap-3 p-4">
                    <CatalogItemThumbnail item={item} size="lg" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium leading-snug text-slate-900">{item.name}</p>
                      {item.category && (
                        <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                          {item.category}
                        </span>
                      )}
                      {item.description && (
                        <p className="mt-1.5 line-clamp-2 text-sm text-slate-500">{item.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-auto flex items-center justify-between border-t border-slate-100 px-4 py-3">
                    <span className="text-sm font-semibold text-slate-900">{money(item.price)}</span>
                    <QuantityStepper
                      quantity={totalQuantityForItem(item.id)}
                      onChange={(qty) => (qty > totalQuantityForItem(item.id) ? incrementItem(item.id) : decrementItem(item.id))}
                    />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <Card className="sticky top-6">
            <CardHeader>
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <ShoppingCart className="h-4 w-4" /> Your order
                {cartItemCount > 0 && (
                  <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-700">
                    {cartItemCount}
                  </span>
                )}
              </span>
            </CardHeader>
            <CardBody className="flex flex-col gap-4">
              {error && <Alert tone="error">{error}</Alert>}

              {resolvedCartLines.length === 0 ? (
                <p className="text-sm text-slate-400">No items added yet.</p>
              ) : (
                <ul className="flex flex-col gap-3 text-sm">
                  {resolvedCartLines.map(({ line, item }) => (
                    <li key={line.id} className="flex flex-col gap-1.5 border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate text-slate-700">{item.name}</span>
                        <span className="shrink-0 font-medium text-slate-900">{money(Number(item.price) * line.quantity)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MiniStepper quantity={line.quantity} onChange={(q) => updateLineQuantity(line.id, q)} />
                        <input
                          type="text"
                          placeholder="Who is this for? (optional)"
                          value={line.reference}
                          onChange={(e) => updateLineReference(line.id, e.target.value)}
                          className="min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500/40"
                        />
                        <button
                          type="button"
                          onClick={() => duplicateLine(line.id)}
                          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-brand-600"
                          aria-label={`Add another ${item.name} as a separate line`}
                          title="Add another for someone else"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => saveLineForLater(line.id)}
                          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-brand-600"
                          aria-label={`Save ${item.name} for later`}
                          title="Save for later"
                        >
                          <Bookmark className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLine(line.id)}
                          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-red-600"
                          aria-label={`Remove ${item.name} from your order`}
                          title="Remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex justify-between border-t border-slate-100 pt-3 text-sm font-semibold text-slate-900">
                <span>Total</span>
                <span>{money(cartTotal)}</span>
              </div>

              <Input
                label="Who is this order for?"
                placeholder="e.g. Jane Smith"
                hint="An overall label for the order. Use the field under each item above if different items are for different people."
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />

              <Button onClick={handleSubmit} isLoading={mutation.isPending} disabled={resolvedCartLines.length === 0}>
                Submit order
              </Button>
            </CardBody>
          </Card>

          {savedForLater.length > 0 && (
            <Card>
              <CardHeader>
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <Bookmark className="h-4 w-4" /> Saved for later
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                    {savedForLater.length}
                  </span>
                </span>
              </CardHeader>
              <CardBody className="flex flex-col gap-2">
                {savedForLater.map((line) => {
                  const item = itemById.get(line.itemId);
                  if (!item) return null;
                  return (
                    <div key={line.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="min-w-0 truncate text-slate-700">
                        {line.quantity} &times; {item.name}
                      </span>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button size="sm" variant="secondary" onClick={() => moveSavedToCart(line.id)}>
                          Move to order
                        </Button>
                        <button
                          type="button"
                          onClick={() => removeSaved(line.id)}
                          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-red-600"
                          aria-label={`Remove ${item.name} from saved items`}
                          title="Remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
